import { ApiResult, HttpClient } from "../http.js";
import { MaterialEntryDto } from "../types/materials.js";

/** Legacy fields shared by create + update payloads. All optional/nullable. */
interface LegacyMaterialFields {
  userId?: string | null;
  userName?: string | null;
  projectTitle?: string | null;
  taskId?: string | null;
  taskTitle?: string | null;
  name?: string | null;
  unitPrice?: number | null;
  totalPrice?: number | null;
  unitSalePrice?: number | null;
  totalSalePrice?: number | null;
  appliedMarkupPercent?: number | null;
  appliedCustomerPriceAdjustment?: number | null;
  appliedCustomerDiscount?: number | null;
  markup?: number | null;
  materialTypeId?: string | null;
  materialTypeName?: string | null;
  tradeGroupId?: string | null;
  tradeGroupName?: string | null;
  supplier?: string | null;
  invoiceNumber?: string | null;
  legacyDate?: string | null;
  billable?: boolean | null;
  budgetCategoryId?: string | null;
  calculationPositionId?: string | null;
  calculationPositionName?: string | null;
  invoicedAt?: string | null;
  invoicedInvoiceId?: string | null;
  invoicedFromProjectId?: string | null;
  approvedAt?: string | null;
  approvedBy?: string | null;
  approvedByName?: string | null;
  rejectedAt?: string | null;
  rejectedBy?: string | null;
  rejectedByName?: string | null;
  rejectionReason?: string | null;
  approvalNotes?: string | null;
  nemHandelSource?: string | null;
  creditorInvoiceId?: string | null;
}

export interface CreateMaterialPayload extends LegacyMaterialFields {
  projectId: string;
  supplierContactId?: string | null;
  supplierName?: string | null;
  description: string;
  quantity: number;
  unit: string;
  costExclVat: number;
  vatRate: number;
  salePriceExclVat?: number | null;
  entryDate: string;
  id?: string;
  /** Economy cost type key; omitted = "materialer". Unknown/inactive keys are a 400. */
  costTypeKey?: string | null;
}

export interface UpdateMaterialPayload extends LegacyMaterialFields {
  supplierContactId?: string | null;
  supplierName?: string | null;
  description: string;
  quantity: number;
  unit: string;
  costExclVat: number;
  vatRate: number;
  salePriceExclVat?: number | null;
  entryDate: string;
  /** Omitted = keep the current cost type. Unknown/inactive keys are a 400. */
  costTypeKey?: string | null;
}

export interface ApproveMaterialPayload {
  approverName: string;
}

export interface RejectMaterialPayload {
  rejectorName: string;
  reason?: string | null;
}

export interface AutoLinkMaterialResult {
  linked: boolean;
}

export interface MoveMaterialPayload {
  newProjectId: string;
  /** Optional; the dialog labels it "Begrundelse (valgfri)". */
  reason?: string | null;
}

export interface RepriceMaterialsPayload {
  projectIds: string[];
}

export interface RepriceMaterialsResult {
  updated: number;
}

export interface MarkMaterialInvoicedPayload {
  invoicedInvoiceId: string;
  invoicedFromProjectId: string;
}

export class MaterialsApi {
  constructor(private readonly http: HttpClient) {}

  list(): Promise<ApiResult<MaterialEntryDto[]>> {
    return this.http.get<MaterialEntryDto[]>("/api/materials");
  }

  listByProject(projectId: string): Promise<ApiResult<MaterialEntryDto[]>> {
    return this.http.get<MaterialEntryDto[]>(`/api/projects/${encodeURIComponent(projectId)}/materials`);
  }

  get(id: string): Promise<ApiResult<MaterialEntryDto>> {
    return this.http.get<MaterialEntryDto>(`/api/materials/${encodeURIComponent(id)}`);
  }

  create(payload: CreateMaterialPayload): Promise<ApiResult<string>> {
    return this.http.post<string>("/api/materials", { body: payload });
  }

  /**
   * Re-runs the material markup chain for every material on the given projects and persists
   * the recomputed sale prices server-side. Returns the count of materials whose price changed.
   * The caller resolves a customer to its linked project ids (via the projects API) first.
   */
  reprice(payload: RepriceMaterialsPayload): Promise<ApiResult<RepriceMaterialsResult>> {
    return this.http.post<RepriceMaterialsResult>("/api/materials/reprice", { body: payload });
  }

  update(id: string, payload: UpdateMaterialPayload): Promise<ApiResult<MaterialEntryDto>> {
    return this.http.put<MaterialEntryDto>(`/api/materials/${encodeURIComponent(id)}`, { body: payload });
  }

  remove(id: string): Promise<ApiResult<boolean>> {
    return this.http.delete<boolean>(`/api/materials/${encodeURIComponent(id)}`);
  }

  /**
   * Moves one material entry to another project.
   *
   * `reason` is optional — the move dialog offers it as "Begrundelse (valgfri)".
   * Refuses (409) when the entry is already invoiced or already on the target project.
   */
  move(id: string, payload: MoveMaterialPayload): Promise<ApiResult<MaterialEntryDto>> {
    return this.http.post<MaterialEntryDto>(
      `/api/materials/${encodeURIComponent(id)}/move`,
      { body: payload },
    );
  }

  approve(id: string, payload: ApproveMaterialPayload): Promise<ApiResult<MaterialEntryDto>> {
    return this.http.post<MaterialEntryDto>(`/api/materials/${encodeURIComponent(id)}/approve`, { body: payload });
  }

  reject(id: string, payload: RejectMaterialPayload): Promise<ApiResult<MaterialEntryDto>> {
    return this.http.post<MaterialEntryDto>(`/api/materials/${encodeURIComponent(id)}/reject`, { body: payload });
  }

  submit(id: string): Promise<ApiResult<MaterialEntryDto>> {
    return this.http.post<MaterialEntryDto>(`/api/materials/${encodeURIComponent(id)}/submit`);
  }

  /**
   * Stamps a material as invoiced (InvoicedAt/InvoicedInvoiceId/InvoicedFromProjectId) via a
   * dedicated minimal endpoint — used by the "Opret faktura" (create-invoice-from-project) flow.
   */
  markInvoiced(id: string, payload: MarkMaterialInvoicedPayload): Promise<ApiResult<MaterialEntryDto>> {
    return this.http.post<MaterialEntryDto>(`/api/materials/${encodeURIComponent(id)}/mark-invoiced`, { body: payload });
  }

  autoLink(id: string): Promise<ApiResult<AutoLinkMaterialResult>> {
    return this.http.post<AutoLinkMaterialResult>(`/api/materials/${encodeURIComponent(id)}/auto-link`);
  }
}
